import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Save, Settings as SettingsIcon, ArrowLeft, Mail } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { SALES_AGENT_SETTINGS_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { useSalesAgentAccess } from "./shared";

/**
 * Sales Manager Intelligence -> Settings (trustedge sales_agent/).
 *
 * Briefing schedule, AI on/off and budget (the AI runs in the local AI worker),
 * the shared thresholds, the priority formula's weights, and each alert rule's
 * on/off, threshold and severity.
 */

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SEVERITIES = ["critical", "high", "medium", "low"];

function Toggle({ on, onClick, disabled, label }) {
    return (
        <button type="button" disabled={disabled} onClick={onClick} aria-pressed={on} aria-label={label}
            className={cn("relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors",
                on ? "bg-primary" : "bg-gray-300 dark:bg-gray-600", disabled && "opacity-50 cursor-not-allowed")}>
            <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                on ? "translate-x-6" : "translate-x-1")} />
        </button>
    );
}

function Row({ title, help, children }) {
    return (
        <div className="flex flex-col gap-2 border-b border-gray-100 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between dark:border-gray-700/60">
            <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">{title}</p>
                {help && <p className="max-w-xl text-xs text-gray-500 dark:text-gray-400">{help}</p>}
            </div>
            <div className="shrink-0">{children}</div>
        </div>
    );
}

const input = "h-8 rounded-md border border-gray-300 px-2 text-sm dark:border-gray-600 dark:bg-gray-800";

export default function SalesAgentSettings() {
    const { fetch, update } = useApiMethods();
    const navigate = useNavigate();
    const { canEditSettings: canEdit } = useSalesAgentAccess();
    const [saved, setSaved] = useState(null);
    const [draft, setDraft] = useState(null);
    const [rules, setRules] = useState([]);
    const [savedRules, setSavedRules] = useState([]);
    const [usage, setUsage] = useState({});
    const [models, setModels] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetch(SALES_AGENT_SETTINGS_URL).then(res => {
            if (res?.status === "success") {
                setSaved(res.data); setDraft(res.data); setRules(res.rules); setSavedRules(res.rules);
                setUsage(res.usage || {}); setModels(res.options?.models || []);
            } else ToastMsg("error", res?.message || "Could not load settings");
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!draft) return <ContainerCard><div className="p-6 text-xs text-gray-400">Loading…</div></ContainerCard>;

    const set = (key, value) => setDraft(d => ({ ...d, [key]: value }));
    const setRule = (key, field, value) => setRules(rs => rs.map(r => r.key === key ? { ...r, [field]: value } : r));
    const dirty = JSON.stringify(saved) !== JSON.stringify(draft) || JSON.stringify(rules) !== JSON.stringify(savedRules);
    const toggleDay = (d) => set("working_days", draft.working_days.includes(d)
        ? draft.working_days.filter(x => x !== d) : [...draft.working_days, d].sort());
    const weightTotal = Number(draft.weight_severity) + Number(draft.weight_value) + Number(draft.weight_urgency);

    const save = async () => {
        setSaving(true);
        try {
            const res = await update(SALES_AGENT_SETTINGS_URL, {
                ...draft,
                rules: rules.filter(r => r.available).map(r => ({
                    key: r.key, enabled: r.enabled, threshold: r.threshold, severity: r.severity,
                })),
            });
            if (res?.status === "success") {
                setSaved(res.data); setDraft(res.data); setRules(res.rules); setSavedRules(res.rules);
                ToastMsg("success", "Saved. Alert rules use the new settings from their next run.");
            } else ToastMsg("error", res?.message || "Could not save");
        } finally { setSaving(false); }
    };

    return (
        <ContainerCard>
            <div className="mx-auto max-w-5xl space-y-6 p-1 sm:p-2">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                        <button type="button" onClick={() => navigate("/sales-agent")} aria-label="Back"
                            className="mt-0.5 rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700">
                            <ArrowLeft className="h-4 w-4" />
                        </button>
                        <div>
                            <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
                                <SettingsIcon className="h-4 w-4 text-primary" /> Sales Agent settings
                            </h1>
                            <p className="text-xs text-gray-500">All figures and alerts come from fixed rules. The AI only writes the briefing's wording, on this organisation's AI worker PC.</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => navigate("/sales-agent/daily-email")}
                            className="inline-flex h-9 items-center gap-2 rounded-md border border-gray-300 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
                            <Mail className="h-4 w-4" /> Daily email
                        </button>
                        {canEdit && (
                            <button type="button" onClick={save} disabled={!dirty || saving}
                                className="inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-white disabled:opacity-50">
                                <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
                            </button>
                        )}
                    </div>
                </div>

                <section>
                    <h2 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">Daily briefing</h2>
                    <Row title="Write a daily briefing" help="On working days, after the time below (IST).">
                        <Toggle on={draft.briefing_enabled} disabled={!canEdit} label="Daily briefing" onClick={() => set("briefing_enabled", !draft.briefing_enabled)} />
                    </Row>
                    <Row title="Briefing time">
                        <input type="time" value={draft.briefing_time} disabled={!canEdit} onChange={e => set("briefing_time", e.target.value)} className={input} />
                    </Row>
                    <Row title="Working days" help="No briefing on other days; yesterday's figures skip them.">
                        <div className="flex flex-wrap gap-1">
                            {DAYS.map((d, i) => (
                                <button key={d} type="button" disabled={!canEdit} onClick={() => toggleDay(i)} aria-pressed={draft.working_days.includes(i)}
                                    className={cn("h-8 w-11 rounded-md border text-xs", draft.working_days.includes(i)
                                        ? "border-primary bg-primary/10 text-primary" : "border-gray-300 text-gray-500 dark:border-gray-600")}>{d}</button>
                            ))}
                        </div>
                    </Row>
                    <Row title="A briefing per sales team" help="Also one for each sales team with a manager (Settings → Lead Assignment → Teams).">
                        <Toggle on={draft.team_briefings} disabled={!canEdit} label="Team briefings" onClick={() => set("team_briefings", !draft.team_briefings)} />
                    </Row>
                </section>

                <section>
                    <h2 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">AI</h2>
                    <Row title="AI-written narrative" help="Off: briefings are rule-based only. Runs in the local AI worker (tick Sales briefings there).">
                        <Toggle on={draft.ai_enabled} disabled={!canEdit} label="AI narrative" onClick={() => set("ai_enabled", !draft.ai_enabled)} />
                    </Row>
                    <Row title="Model">
                        <select value={draft.model} disabled={!canEdit} onChange={e => set("model", e.target.value)} className={input}>
                            {models.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </Row>
                    <Row title="Monthly AI budget (USD)" help={`Used this month: USD ${usage.month_cost_usd ?? 0}. When reached, briefings stay rule-based until next month.`}>
                        <input type="number" min="0" step="1" value={draft.monthly_budget_usd} disabled={!canEdit}
                            onChange={e => set("monthly_budget_usd", e.target.value)} className={cn(input, "w-24")} />
                    </Row>
                </section>

                <section>
                    <h2 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">Alerts and priority</h2>
                    <Row title="High-value deal (₹)" help="Deals at or above this quoted value count as high value in the rules and the priority score.">
                        <input type="number" min="0" step="1000" value={draft.high_value_threshold} disabled={!canEdit}
                            onChange={e => set("high_value_threshold", e.target.value)} className={cn(input, "w-32")} />
                    </Row>
                    <Row title="Ignore leads untouched for (days)" help="Lead alerts skip leads with nothing logged for this long, so dead stock does not bury today's work.">
                        <input type="number" min="1" value={draft.ignore_older_than_days} disabled={!canEdit}
                            onChange={e => set("ignore_older_than_days", e.target.value)} className={cn(input, "w-24")} />
                    </Row>
                    <Row title="Dismissed alerts stay quiet for (hours)">
                        <input type="number" min="0" value={draft.dismiss_snooze_hours} disabled={!canEdit}
                            onChange={e => set("dismiss_snooze_hours", e.target.value)} className={cn(input, "w-24")} />
                    </Row>
                    <Row title="Priority weights" help={`Score = severity × weight + deal value × weight + urgency × weight, scaled to 100 (now totalling ${weightTotal}).`}>
                        <div className="flex gap-2 text-xs text-gray-500">
                            {[["weight_severity", "Severity"], ["weight_value", "Value"], ["weight_urgency", "Urgency"]].map(([k, l]) => (
                                <label key={k} className="flex flex-col items-center gap-0.5">
                                    <input type="number" min="0" max="100" value={draft[k]} disabled={!canEdit}
                                        onChange={e => set(k, e.target.value)} className={cn(input, "w-16")} />{l}
                                </label>
                            ))}
                        </div>
                    </Row>
                    <Row title="Notify on new critical alerts" help="An in-app notification to the alert's owner, or to admins when nobody owns it. Once per alert.">
                        <Toggle on={draft.notify_critical} disabled={!canEdit} label="Notify critical" onClick={() => set("notify_critical", !draft.notify_critical)} />
                    </Row>
                </section>

                <section>
                    <h2 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Alert rules</h2>
                    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="w-full min-w-[40rem] text-xs">
                            <thead className="bg-gray-50 text-left text-[10px] uppercase text-gray-500 dark:bg-gray-800">
                                <tr><th className="px-3 py-2">#</th><th className="py-2">Rule</th><th>On</th><th>Threshold</th><th className="pr-3">Severity</th></tr>
                            </thead>
                            <tbody>
                                {rules.map(r => (
                                    <tr key={r.key} className={cn("border-t border-gray-100 align-top dark:border-gray-700", !r.available && "opacity-60")}>
                                        <td className="px-3 py-2 text-gray-400">{r.number}</td>
                                        <td className="py-2 pr-3">
                                            <p className="font-medium text-gray-900 dark:text-white">{r.name}{!r.available && <span className="ml-1 text-[10px] font-normal text-gray-400">planned</span>}</p>
                                            <p className="text-gray-500">{r.description}</p>
                                        </td>
                                        <td className="py-2"><Toggle on={r.enabled} disabled={!canEdit || !r.available} label={`${r.name} on`} onClick={() => setRule(r.key, "enabled", !r.enabled)} /></td>
                                        <td className="py-2">
                                            {r.default_threshold != null ? (
                                                <span className="inline-flex items-center gap-1">
                                                    <input type="number" min="0" value={r.threshold ?? ""} disabled={!canEdit || !r.available}
                                                        onChange={e => setRule(r.key, "threshold", e.target.value === "" ? null : Number(e.target.value))}
                                                        className={cn(input, "h-7 w-20 text-xs")} />
                                                    <span className="text-gray-400">{r.unit}</span>
                                                </span>
                                            ) : <span className="text-gray-400">—</span>}
                                        </td>
                                        <td className="py-2 pr-3">
                                            <select value={r.severity} disabled={!canEdit || !r.available} onChange={e => setRule(r.key, "severity", e.target.value)}
                                                className={cn(input, "h-7 text-xs capitalize")}>
                                                {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="mt-2 text-[11px] text-gray-400">Rules run every 30 minutes{usage.rules_last_evaluated ? `; last run ${usage.rules_last_evaluated.replace("T", " ")}` : ""}. A rule switched off closes its open alerts.</p>
                </section>
            </div>
        </ContainerCard>
    );
}
